import { styled } from '@mui/material/styles';
import type { SxProps } from '@mui/system';
import { excludeProps } from '@shared/theme/util';
import { forwardRef, ReactNode } from 'react';
import { Skeleton } from '@/component/Skeleton';
import { getImgSrcWithSize } from '@/util/imageUtil';
import ImageModal from './ImageModal';

const sizes = {
  thumbnail: 128,
  md: 256,
  lg: 512,
  original: '100%',
};

const ForwardRefImg = forwardRef<HTMLImageElement, any>(function ForwardRefImg(
  { children: _children, alt, ...props },
  ref
) {
  return <img ref={ref} alt={alt} {...props} />;
});

interface ImgProps {
  square: boolean;
  size?: ImageSize;
  minSize: ImageSize;
}

const Img = styled(ForwardRefImg, {
  name: 'Image',
  shouldForwardProp: (propName) =>
    excludeProps(propName, ['square', 'size', 'minSize', 'sx']),
})<ImgProps>(({ size, minSize, square, theme }) =>
  theme.unstable_sx([
    !square &&
      size === 'thumbnail' && {
        maxWidth: sizes.thumbnail,
        maxHeight: sizes.thumbnail,
      },
    !square &&
      size === 'md' && {
        maxWidth: `clamp(128px, ${sizes.md}px, 100%)`,
        maxHeight: sizes.md,
      },
    !square &&
      size === 'lg' && {
        maxWidth: `clamp(128px, ${sizes.lg}px, 100%)`,
        maxHeight: sizes.lg,
      },

    square && { objectFit: 'cover' },
    square &&
      (size === 'thumbnail' || size === undefined) && {
        width: sizes.thumbnail,
        height: sizes.thumbnail,
      },
    square &&
      size === 'md' && {
        width: sizes.md,
        height: sizes.md,
      },
    square &&
      size === 'lg' && {
        width: sizes.lg,
        height: sizes.lg,
      },

    minSize === 'thumbnail' && {
      minWidth: sizes.thumbnail,
    },
    minSize === 'md' && {
      minWidth: sizes.md,
    },
    minSize === 'lg' && {
      minWidth: sizes.lg,
    },
  ])
);

export interface ImageProps {
  src?: string;
  alt: string;
  size?: ImageSize;
  minSize?: ImageSize;
  square?: boolean;
  modal?: boolean;
  className?: string;
  onDelete?: () => void;
  sx?: SxProps;
  isLoading?: boolean;
  modalOverlay?: ReactNode;
  open?: boolean;
  setOpen?: (open: boolean) => void;
}

const Image = forwardRef<HTMLImageElement, ImageProps>(function Image(
  {
    className,
    modal,
    src = 'original',
    alt,
    size,
    minSize = 'thumbnail',
    square = false,
    onDelete,
    sx,
    ...props
  },
  ref
) {
  if (modal) {
    return (
      <ImageModal
        src={src}
        alt={alt}
        className={className}
        size={size}
        minSize={minSize}
        square={square}
        onDelete={onDelete}
        sx={sx}
        {...props}
      />
    );
  }

  const srcWithFormat = formatSrc(src, size);

  if (props.isLoading) {
    return <Skeleton width="100%" height="100%" variant="rectangular" />;
  }

  return (
    <Img
      src={srcWithFormat}
      alt={alt}
      className={className}
      loading="lazy"
      ref={ref}
      size={size}
      minSize={minSize}
      square={square}
      sx={sx}
    />
  );
});

function formatSrc(src: string, size: ImageSize = 'original') {
  if (size === 'original') {
    return src;
  }
  const sizeInPx = sizes[size];
  return getImgSrcWithSize(src, sizeInPx);
}

export default Image;
